import * as Y from 'yjs';
import {
  TREE_JSON_FORMAT,
  TREE_JSON_VERSION,
  TreeJsonSchema,
  countJsonNodes,
  MAX_TREE_NODES,
  normalizeValue,
  type Criterion,
  type EstimatedBy,
  type NodeJson,
  type NodeValue,
  type Tree,
  type TreeJson,
  type ValueInput,
} from '@forkcast/shared';
import { DocError } from './errors';
import { newId, slugify } from './ids';
import { generateKeyBetween, generateNKeysBetween } from './order';
import { makeCriterionMap, makeNodeMap } from './nodes';
import { getActivity, getCriteria, getMeta, getNodes, SCHEMA_VERSION, type Origin } from './schema';
import { buildSnapshot } from './snapshot';

export function parseTreeJson(input: unknown): TreeJson {
  const parsed = TreeJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new DocError(
      'VALIDATION',
      `Invalid tree JSON: ${parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`).join('; ')}`,
      parsed.error.issues,
    );
  }
  return parsed.data;
}

function valueToJson(v: NodeValue): ValueInput {
  return v.estimatedBy ? { value: v.value, estimatedBy: v.estimatedBy } : v.value;
}

/** Nested JSON of a node and its descendants. */
export function nodeToJson(tree: Tree, nodeId: string): NodeJson {
  const node = tree.nodes[nodeId];
  if (!node) throw new DocError('NODE_NOT_FOUND', `Node "${nodeId}" does not exist.`, { nodeId });
  const json: NodeJson = { id: node.id, label: node.label, kind: node.kind };
  if (node.optional) json.optional = true;
  if (node.notes) json.notes = node.notes;
  const entries = Object.entries(node.values);
  if (entries.length > 0) json.values = Object.fromEntries(entries.map(([k, v]) => [k, valueToJson(v)]));
  const kids = tree.children[nodeId] ?? [];
  if (kids.length > 0) json.children = kids.map((k) => nodeToJson(tree, k));
  return json;
}

export function treeToJson(tree: Tree): TreeJson {
  const criteria = tree.criteria.map((c) => {
    const out: TreeJson['criteria'][number] = {
      id: c.id,
      label: c.label,
      aggregation: c.aggregation,
      direction: c.direction,
      order: c.order,
    };
    if (c.unit) out.unit = c.unit;
    return out;
  });
  return {
    format: TREE_JSON_FORMAT,
    version: TREE_JSON_VERSION,
    title: tree.meta.title,
    criteria,
    root: tree.meta.rootId ? nodeToJson(tree, tree.meta.rootId) : { label: tree.meta.title, kind: 'and' },
  };
}

export function docToJson(doc: Y.Doc): TreeJson {
  return treeToJson(buildSnapshot(doc));
}

export interface ImportOptions {
  /** Keep the ids found in the JSON (default true). Fresh ids are generated otherwise. */
  keepIds?: boolean;
  /** Override the title. */
  title?: string;
  /** Flag every imported value (e.g. `ai` for trees generated by an assistant). */
  estimatedBy?: EstimatedBy;
}

export interface ImportResult {
  rootId: string;
  nodeCount: number;
  criteriaIds: string[];
}

/** Resolve criteria ids (slug from label when missing, de-duplicated). */
function resolveCriteria(inits: TreeJson['criteria']): Criterion[] {
  const used = new Set<string>();
  return inits.map((init, i) => {
    let id = init.id ?? slugify(init.label);
    const base = id;
    let n = 2;
    while (used.has(id)) id = `${base}-${n++}`;
    used.add(id);
    const c: Criterion = {
      id,
      label: init.label,
      aggregation: init.aggregation ?? 'sum',
      direction: init.direction ?? 'minimize',
      order: init.order ?? i,
    };
    if (init.unit) c.unit = init.unit;
    return c;
  });
}

function checkValues(node: NodeJson, known: Set<string>, path: string): void {
  for (const cid of Object.keys(node.values ?? {})) {
    if (!known.has(cid)) {
      throw new DocError('CRITERION_NOT_FOUND', `Unknown criterion "${cid}" at ${path}.values. Declare it in "criteria" first. Known: ${[...known].join(', ') || 'none'}.`, {
        criterionId: cid,
        path,
        known: [...known],
      });
    }
  }
  (node.children ?? []).forEach((c, i) => checkValues(c, known, `${path}.children[${i}]`));
}

/**
 * Replace the whole content of a document with the given JSON (validated), in one
 * transaction. Used for file import, URL sharing and the MCP `import_tree` tool.
 */
export function importJson(doc: Y.Doc, input: unknown, origin: Origin = 'import', options: ImportOptions = {}): ImportResult {
  const json = parseTreeJson(input);
  const nodeCount = countJsonNodes(json.root);
  if (nodeCount > MAX_TREE_NODES) {
    throw new DocError('LIMIT_EXCEEDED', `The tree holds ${nodeCount} nodes, above the limit of ${MAX_TREE_NODES}.`, { limit: MAX_TREE_NODES, total: nodeCount });
  }
  const criteria = resolveCriteria(json.criteria);
  const known = new Set(criteria.map((c) => c.id));
  checkValues(json.root, known, 'root');
  const keepIds = options.keepIds ?? true;
  const used = new Set<string>();
  const assign = (given: string | undefined): string => {
    let id = keepIds && given && !used.has(given) ? given : newId();
    while (used.has(id)) id = newId();
    used.add(id);
    return id;
  };

  let rootId = '';
  doc.transact(() => {
    const meta = getMeta(doc);
    const criteriaMap = getCriteria(doc);
    const nodes = getNodes(doc);
    const activity = getActivity(doc);
    for (const key of Array.from(criteriaMap.keys())) criteriaMap.delete(key);
    for (const key of Array.from(nodes.keys())) nodes.delete(key);
    if (activity.length > 0) activity.delete(0, activity.length);

    for (const c of criteria) criteriaMap.set(c.id, makeCriterionMap(c));

    const build = (node: NodeJson, parentId: string | null, orderKey: string): string => {
      const id = assign(node.id);
      const kids = node.children ?? [];
      const values: Record<string, NodeValue> = {};
      for (const [cid, v] of Object.entries(node.values ?? {})) values[cid] = normalizeValue(v, options.estimatedBy);
      nodes.set(
        id,
        makeNodeMap({
          id,
          parentId,
          orderKey,
          label: node.label,
          kind: node.kind ?? (kids.length > 0 ? 'and' : 'leaf'),
          optional: parentId === null ? undefined : node.optional,
          notes: node.notes,
          values,
        }),
      );
      const keys = generateNKeysBetween(null, null, kids.length);
      kids.forEach((k, i) => build(k, id, keys[i]!));
      return id;
    };
    rootId = build(json.root, null, generateKeyBetween(null, null));

    meta.set('schemaVersion', SCHEMA_VERSION);
    meta.set('title', options.title ?? json.title);
    meta.set('rootId', rootId);
  }, origin);

  return { rootId, nodeCount, criteriaIds: criteria.map((c) => c.id) };
}

export function createDocFromJson(input: unknown, options: ImportOptions = {}): Y.Doc {
  const doc = new Y.Doc();
  importJson(doc, input, 'import', options);
  return doc;
}

/** Binary state helpers (base64), used to seed server-side documents. */
export function encodeDocState(doc: Y.Doc): Uint8Array {
  return Y.encodeStateAsUpdate(doc);
}

export function docFromState(state: Uint8Array): Y.Doc {
  const doc = new Y.Doc();
  Y.applyUpdate(doc, state);
  return doc;
}
